import * as React from 'react';
import { createContext, useContext, useMemo } from 'react';

import { useExperiment } from '@coursera-web/statsig';

import { breakpoints, useMediaQuery } from '@coursera/cds-core';

import useUserAgent from 'bundles/page/hooks/useUserAgent';
import { CoachStateProvider } from 'bundles/unified-common/providers/CoachStateProvider';
import { TwoPanelChatRouteProvider } from 'bundles/unified-common/providers/TwoPanelChatRouteProvider';
import { useIsCourseDataLoading } from 'bundles/unified-description-page-common/contexts/consumer/ConsumerBffCourseContext';
import { useIsS12nDataLoading } from 'bundles/unified-description-page-common/contexts/consumer/ConsumerBffSpecializationContext';
import useIsDesktop from 'bundles/unified-description-page-common/hooks/useIsDesktop';

const EXPERIMENT_NAME = 'consumer-xdp-two-panel-convo-disco';
const PARAM_NAME = 'chat-panel-location';
const CHAT_ON_LEFT = 'left';
const CHAT_ON_RIGHT = 'right';
const NO_CHAT_PANEL = 'none';

export type TwoPanelConversationalDiscoveryExperimentContextType = {
  twoPanelConversationalDiscoveryEnabled: boolean;
  chatOnLeft: boolean;
  chatOnRight: boolean;
};

export const TwoPanelConversationalDiscoveryExperimentContext =
  createContext<TwoPanelConversationalDiscoveryExperimentContextType>({
    twoPanelConversationalDiscoveryEnabled: false,
    chatOnLeft: false,
    chatOnRight: false,
  });

function TwoPanelConversationalDiscoveryExperimentProvider({ children }: React.PropsWithChildren) {
  const isS12nDataLoading = useIsS12nDataLoading();
  const isCourseDataLoading = useIsCourseDataLoading();

  // Drives the layout. UA-seeded so real desktop users render the panel on first
  // paint (no control→treatment flash); collapses to single-panel below 1024px.
  const isDesktop = useIsDesktop();

  // Suppress exposure for mobile/tablet UAs (never desktop, so they must not
  // affect allocation) and for viewports <1024px — seeded pessimistically (assume
  // below desktop until measured) so a desktop window <1024px never logs a stray
  // exposure. useExperiment re-logs when this flips, so no manual logging needed.
  const userAgent = useUserAgent();
  const isMobileOrTabletUserAgent = userAgent?.isMobileBrowser || userAgent?.isMobile;
  const isBelowDesktopWidth = useMediaQuery(breakpoints.down('sm'), { defaultMatches: true });
  const suppressExposure = isMobileOrTabletUserAgent || isBelowDesktopWidth;
  const experiment = useExperiment(EXPERIMENT_NAME, { disableExposureLog: suppressExposure });
  const location = isDesktop ? experiment.get(PARAM_NAME, NO_CHAT_PANEL) : NO_CHAT_PANEL;

  const value = useMemo(() => {
    const chatOnLeft = location === CHAT_ON_LEFT;
    const chatOnRight = location === CHAT_ON_RIGHT;
    return {
      twoPanelConversationalDiscoveryEnabled: chatOnLeft || chatOnRight,
      chatOnLeft,
      chatOnRight,
    };
  }, [location]);

  return (
    <TwoPanelConversationalDiscoveryExperimentContext.Provider value={value}>
      {value.twoPanelConversationalDiscoveryEnabled ? (
        <CoachStateProvider coachOpen={true} isContextConfigLoading={isS12nDataLoading || isCourseDataLoading}>
          <TwoPanelChatRouteProvider>{children}</TwoPanelChatRouteProvider>
        </CoachStateProvider>
      ) : (
        children
      )}
    </TwoPanelConversationalDiscoveryExperimentContext.Provider>
  );
}

export const useTwoPanelConversationalDiscoveryExperiment = () => {
  return useContext(TwoPanelConversationalDiscoveryExperimentContext);
};

export default TwoPanelConversationalDiscoveryExperimentProvider;
